import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Wrench, Plus, FilePdf, Sparkle, Camera, CheckSquare, Square, Check,
  ArrowRight, ListChecks, Shield, Warning, Trash, UploadSimple, FloppyDisk,
  MagnifyingGlass, PencilSimple, WhatsappLogo, ClipboardText, UserPlus, Eye,
} from '@phosphor-icons/react';
import ReportToolbar from '../../components/ReportToolbar';
import ServiceReportDialog from '../../components/job-orders/ServiceReportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = ['draft', 'assigned', 'in_progress', 'completed_by_site', 'approved'];
const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  assigned: 'bg-blue-50 text-blue-700 border-blue-300',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-300',
  completed_by_site: 'bg-violet-50 text-violet-700 border-violet-300',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  cancelled: 'bg-red-50 text-red-700 border-red-300',
};

const SERVICE_TYPES = [
  { value: 'one_time', label: 'One-time service' },
  { value: 'ppm', label: 'PPM (Planned Preventive)' },
];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const LAYOUTS = ['classic', 'modern_band', 'minimalist', 'compact'];

function getToken() {
  return localStorage.getItem('erp_token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('token');
}

// =================== Inline Customer Dialog ===================
function CustomerEditorDialog({ open, onOpenChange, existing, onSaved }) {
  const blank = { name: '', company_name: '', phone: '', email: '', address: '', tax_id: '', country: '', contact_type: 'customer' };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(existing ? { ...blank, ...existing } : { ...blank });
  }, [open, existing]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      if (existing?.id) {
        const res = await axios.patch(`${API}/contacts/${existing.id}`, form);
        toast.success('Customer updated');
        onSaved?.(res.data);
      } else {
        const res = await axios.post(`${API}/contacts`, form);
        toast.success('Customer added');
        onSaved?.(res.data);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="customer-editor">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
          <DialogDescription>
            Customer is saved to your Contacts list and will be available in every future Job Order, Quotation, and Invoice.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label>Name <span className="text-red-500">*</span></Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="cust-name" />
          </div>
          <div>
            <Label>Company name</Label>
            <Input value={form.company_name || ''} onChange={e => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div>
            <Label>Tax ID / TRN</Label>
            <Input value={form.tax_id || ''} onChange={e => setForm({ ...form, tax_id: e.target.value })} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+971…" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Address</Label>
            <Textarea rows={2} value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="cust-save">{existing ? 'Update' : 'Add Customer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================== Customer Combobox (search dropdown) ===================
function CustomerCombobox({ customers, value, onPick, onAdd, onEdit }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = customers.find(c => c.id === value);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return customers.slice(0, 25);
    return customers.filter(c =>
      (c.name || '').toLowerCase().includes(t)
      || (c.company_name || '').toLowerCase().includes(t)
      || (c.phone || '').toLowerCase().includes(t)
      || (c.email || '').toLowerCase().includes(t)
    ).slice(0, 25);
  }, [customers, q]);

  return (
    <div className="relative" data-testid="cust-combobox">
      <div className="flex items-center gap-1">
        <Input
          placeholder="Search customer…"
          value={open ? q : (selected?.name || '')}
          onFocus={() => { setOpen(true); setQ(''); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={e => setQ(e.target.value)}
          data-testid="cust-search-input"
          className="flex-1"
        />
        {selected && (
          <Button type="button" size="icon" variant="ghost" onClick={() => onEdit(selected)} title="Edit customer" data-testid="cust-edit-btn">
            <PencilSimple size={14} />
          </Button>
        )}
        <Button type="button" size="icon" variant="ghost" onClick={() => onAdd()} title="Add new customer" data-testid="cust-add-btn">
          <UserPlus size={14} />
        </Button>
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-72 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              No customer found.
              <button className="text-blue-600 ml-2 underline" onMouseDown={() => onAdd(q)}>
                + Add "{q}"
              </button>
            </div>
          ) : filtered.map(c => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-border/40 last:border-0"
              onMouseDown={() => { onPick(c); setOpen(false); }}
              data-testid={`cust-opt-${c.id}`}
            >
              <div className="font-medium">{c.name}</div>
              {(c.company_name || c.phone || c.email) && (
                <div className="text-muted-foreground text-[10px] truncate">
                  {[c.company_name, c.phone, c.email].filter(Boolean).join(' · ')}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ========================= Main Page =========================
export default function JobOrdersPage() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState({ defaults: [], custom: [] });
  const [invoices, setInvoices] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [working, setWorking] = useState(false);
  const [aiBusy, setAiBusy] = useState({ checklist: false, mos: false, ra: false });
  const [activeTab, setActiveTab] = useState('details'); // details | checklist | mos | service-reports
  const [custEditorOpen, setCustEditorOpen] = useState(false);
  const [custEditing, setCustEditing] = useState(null);
  const [srOpen, setSrOpen] = useState(false);
  const [srEditingId, setSrEditingId] = useState(null);
  const [serviceReports, setServiceReports] = useState([]);

  const blank = {
    customer_id: '',
    customer_name: '',
    site_location: '',
    service_type: 'one_time',
    equipment_types: [],
    scheduled_date: new Date().toISOString().slice(0, 10),
    scheduled_start_date: '',
    scheduled_end_date: '',
    actual_start_date: '',
    actual_end_date: '',
    priority: 'normal',
    assigned_to_user_id: '',
    assigned_to_user_name: '',
    assigned_to_name: '',
    linked_invoice_id: '',
    linked_invoice_number: '',
    linked_invoices: [],
    linked_vouchers: [],
    linked_lpo_number: '',
    scope_of_work: '',
    safety_notes: '',
    method_of_statement: '',
    risk_assessment: '',
    checklist: [],
    materials: [],
    team_members: [],
    custom_status: '',
    status_notes: '',
    layout_template: 'classic',
  };
  const [form, setForm] = useState(blank);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [jRes, eRes, iRes, cRes, uRes, vRes] = await Promise.all([
        axios.get(`${API}/job-orders/`),
        axios.get(`${API}/job-orders/equipment-types`),
        axios.get(`${API}/invoices`).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts?contact_type=customer`).catch(() => ({ data: [] })),
        axios.get(`${API}/job-orders/company-users`).catch(() => ({ data: [] })),
        axios.get(`${API}/vouchers/payment`).catch(() => ({ data: [] })),
      ]);
      setJobs(jRes.data || []);
      setEquipmentTypes(eRes.data || { defaults: [], custom: [] });
      setInvoices(iRes.data || []);
      setCustomers(cRes.data || []);
      setCompanyUsers(uRes.data || []);
      setVouchers(vRes.data || []);
    } catch (e) {
      toast.error('Failed to load job orders');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredJobs = useMemo(() => {
    const t = search.trim().toLowerCase();
    return (filterStatus === 'all' ? jobs : jobs.filter(j => j.status === filterStatus))
      .filter(j => !t ||
        (j.job_number || '').toLowerCase().includes(t) ||
        (j.customer_name || '').toLowerCase().includes(t) ||
        (j.site_location || '').toLowerCase().includes(t) ||
        (j.assigned_to_name || '').toLowerCase().includes(t) ||
        (j.equipment_types || []).join(' ').toLowerCase().includes(t)
      );
  }, [jobs, filterStatus, search]);

  const allEquipment = [...(equipmentTypes.defaults || []), ...(equipmentTypes.custom || [])];

  const openNew = () => {
    setEditing(null);
    setViewOnly(false);
    setForm({ ...blank });
    setActiveTab('details');
    setServiceReports([]);
    setFormOpen(true);
  };
  const openEdit = async (j) => {
    setEditing(j);
    setViewOnly(false);
    setForm({
      ...blank, ...j,
      checklist: j.checklist || [],
      scheduled_date: j.scheduled_date || '',
    });
    setActiveTab('details');
    setFormOpen(true);
    // load related service reports
    try {
      const res = await axios.get(`${API}/job-orders/${j.id}/service-reports`);
      setServiceReports(res.data || []);
    } catch { setServiceReports([]); }
  };

  const openView = async (j) => {
    // Read-only preview: same dialog, but every input gets disabled via
    // a viewOnly flag consumed by the form section below.
    setEditing(j);
    setViewOnly(true);
    setForm({ ...blank, ...j, checklist: j.checklist || [], scheduled_date: j.scheduled_date || '' });
    setActiveTab('details');
    setFormOpen(true);
    try {
      const res = await axios.get(`${API}/job-orders/${j.id}/service-reports`);
      setServiceReports(res.data || []);
    } catch { setServiceReports([]); }
  };

  const deleteJob = async (j) => {
    const reason = window.prompt(
      `Delete job order ${j.job_number}?\n\nPlease enter a deletion reason (this is recorded in the audit trail).`
    );
    if (!reason || !reason.trim()) return;
    try {
      await axios.delete(`${API}/job-orders/${j.id}`, { params: { reason: reason.trim() } });
      toast.success(`Deleted ${j.job_number}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const save = async () => {
    setWorking(true);
    try {
      let saved;
      if (editing) {
        const res = await axios.patch(`${API}/job-orders/${editing.id}`, form);
        saved = res.data;
        toast.success('Job order updated');
      } else {
        const res = await axios.post(`${API}/job-orders/`, form);
        saved = res.data;
        toast.success(`Created ${saved.job_number}`);
      }
      setEditing(saved);
      setForm(f => ({ ...f, ...saved }));
      fetchAll();
      return saved;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setWorking(false); }
  };

  const saveAndClose = async () => { const s = await save(); if (s) setFormOpen(false); };

  const saveAndDownloadPdf = async () => {
    const s = await save();
    if (s) downloadPdf(s);
  };

  const callAction = async (j, action, extra = '') => {
    try {
      await axios.post(`${API}/job-orders/${j.id}/${action}${extra}`);
      toast.success(`Job ${j.job_number}: ${action.replace(/_/g, ' ')}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    }
  };

  const downloadPdf = (j) => {
    const url = `${API}/job-orders/${j.id}/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `job-order-${j.job_number}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('PDF download failed'));
  };

  const whatsappShare = (j) => {
    const phone = (customers.find(c => c.id === j.customer_id)?.phone || '').replace(/[^\d]/g, '');
    const text = encodeURIComponent(
      `Job Order ${j.job_number}\n` +
      `Customer: ${j.customer_name || ''}\n` +
      `Site: ${j.site_location || ''}\n` +
      `Scheduled: ${j.scheduled_date || ''}\n` +
      `Equipment: ${(j.equipment_types || []).join(', ')}\n` +
      `Status: ${(j.status || '').replace(/_/g, ' ')}`
    );
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  const toggleEquipment = (eq) => {
    setForm(f => ({
      ...f,
      equipment_types: f.equipment_types.includes(eq)
        ? f.equipment_types.filter(x => x !== eq)
        : [...f.equipment_types, eq],
    }));
  };

  const pickCustomer = (c) => {
    setForm(f => ({
      ...f,
      customer_id: c.id,
      customer_name: c.name,
      site_location: f.site_location || c.address || '',
    }));
  };

  // AI helpers
  const aiChecklist = async (eq) => {
    if (!eq) { toast.error('Pick an equipment first'); return; }
    setAiBusy(b => ({ ...b, checklist: true }));
    try {
      const res = await axios.post(`${API}/job-orders/ai/checklist`, {
        equipment_type: eq, service_type: form.service_type,
      });
      setForm(f => ({ ...f, checklist: [...f.checklist, ...res.data.checklist] }));
      toast.success(`Added ${res.data.checklist.length} AI checklist items for ${eq}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI checklist failed'); }
    finally { setAiBusy(b => ({ ...b, checklist: false })); }
  };
  const aiMoS = async () => {
    setAiBusy(b => ({ ...b, mos: true }));
    try {
      const res = await axios.post(`${API}/job-orders/ai/method-of-statement`, {
        scope_of_work: form.scope_of_work || 'General MEP work',
        equipment_types: form.equipment_types,
        site_location: form.site_location,
      });
      const d = res.data;
      const text =
        `Title: ${d.title || ''}\n\n` +
        `Purpose: ${d.purpose || ''}\n\nScope: ${d.scope || ''}\n\n` +
        `Tools / Equipment:\n${(d.equipment_and_tools || []).map(x => '• ' + x).join('\n')}\n\n` +
        `PPE Required:\n${(d.ppe_required || []).map(x => '• ' + x).join('\n')}\n\n` +
        `Step-by-Step Procedure:\n${(d.step_by_step || []).map((x, i) => `${i + 1}. ${x}`).join('\n')}\n\n` +
        `Safety Controls:\n${(d.safety_controls || []).map(x => '• ' + x).join('\n')}\n\n` +
        `Emergency Procedures:\n${(d.emergency_procedures || []).map(x => '• ' + x).join('\n')}`;
      setForm(f => ({ ...f, method_of_statement: text }));
      toast.success('Method of Statement drafted');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI MoS failed'); }
    finally { setAiBusy(b => ({ ...b, mos: false })); }
  };
  const aiRA = async () => {
    setAiBusy(b => ({ ...b, ra: true }));
    try {
      const res = await axios.post(`${API}/job-orders/ai/risk-assessment`, {
        scope_of_work: form.scope_of_work || 'General MEP work',
        equipment_types: form.equipment_types,
        site_location: form.site_location,
      });
      const d = res.data;
      const text =
        `Title: ${d.title || ''}\n\n` +
        `Task: ${d.task_description || ''}\n\nHazards:\n` +
        (d.hazards || []).map((h, i) =>
          `${i + 1}. ${h.hazard} [L=${h.likelihood} S=${h.severity} ${h.risk_rating}]\n` +
          `   At risk: ${h.who_at_risk}\n` +
          `   Controls:\n${(h.control_measures || []).map(c => '   • ' + c).join('\n')}`
        ).join('\n\n');
      setForm(f => ({ ...f, risk_assessment: text }));
      toast.success('Risk Assessment drafted');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI RA failed'); }
    finally { setAiBusy(b => ({ ...b, ra: false })); }
  };

  // Checklist editor
  const addChecklistRow = () => setForm(f => ({
    ...f,
    checklist: [...f.checklist, { id: crypto.randomUUID?.() || String(Math.random()), section: 'General', description: '', checked: false, comment: '' }],
  }));
  const updateChecklistRow = (i, key, val) => setForm(f => ({
    ...f,
    checklist: f.checklist.map((c, idx) => idx === i ? { ...c, [key]: val } : c),
  }));
  const removeChecklistRow = (i) => setForm(f => ({
    ...f, checklist: f.checklist.filter((_, idx) => idx !== i),
  }));

  const uploadFile = async (kind, file) => {
    if (!editing) { toast.error('Save the job order first'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    try {
      await axios.post(`${API}/job-orders/${editing.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${kind}`);
      const refreshed = await axios.get(`${API}/job-orders/${editing.id}`);
      setEditing(refreshed.data);
      setForm(f => ({ ...f, ...refreshed.data }));
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  // ---------- Service Report helpers ----------
  const refreshServiceReports = async () => {
    if (!editing) return;
    try {
      const res = await axios.get(`${API}/job-orders/${editing.id}/service-reports`);
      setServiceReports(res.data || []);
    } catch { /* ignore */ }
  };
  const openNewServiceReport = async () => {
    if (!editing) { toast.error('Save the Job Order first'); return; }
    try {
      const res = await axios.post(`${API}/job-orders/${editing.id}/service-reports`, {});
      toast.success(`Service Report ${res.data.sr_number} created — fill in details`);
      setSrEditingId(res.data.id);
      setSrOpen(true);
      refreshServiceReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create'); }
  };
  const openServiceReport = (sr) => { setSrEditingId(sr.id); setSrOpen(true); };

  // ---------- Equipment custom add ----------
  const addCustomEquipment = async () => {
    const name = window.prompt('New equipment type (e.g. "VFD Drive"):')?.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/job-orders/equipment-types?name=${encodeURIComponent(name)}`);
      toast.success(`Added "${name}"`);
      const eRes = await axios.get(`${API}/job-orders/equipment-types`);
      setEquipmentTypes(eRes.data);
      toggleEquipment(name);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
  };

  return (
    <div className="container mx-auto p-4 lg:p-6" data-testid="job-orders-page">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Wrench size={28} weight="duotone" /> Job Orders
          </h1>
          <p className="text-muted-foreground text-sm">Site work orders, PPM, AI checklists, MoS &amp; Risk Assessment. Generate Service Reports for completed jobs.</p>
        </div>
        <Button onClick={openNew} data-testid="new-job-order-btn"><Plus size={16} className="mr-2" /> New Job Order</Button>
      </motion.div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by JO#, customer, site, equipment…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="jo-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44 h-9" data-testid="jo-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_FLOW.concat(['cancelled']).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <ReportToolbar
          testidPrefix="jo-toolbar"
          downloadBaseName={`job-orders-summary-${new Date().toISOString().slice(0, 10)}`}
          exportUrl={(format) => {
            const params = new URLSearchParams();
            if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
            if (search) params.set('q', search);
            const ext = format === 'xlsx' ? 'excel' : 'pdf';
            const qs = params.toString();
            return `${API}/summary/job_orders/${ext}${qs ? `?${qs}` : ''}`;
          }}
          onPrint={async () => {
            // "Print" opens the same branded PDF inline in a new tab so the
            // user can review before hitting the browser's Print dialog.
            try {
              const token =
                localStorage.getItem('erp_token') ||
                localStorage.getItem('access_token') ||
                localStorage.getItem('token');
              const params = new URLSearchParams({ inline: 'true' });
              if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
              if (search) params.set('q', search);
              const res = await fetch(`${API}/summary/job_orders/pdf?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const win = window.open(url, '_blank');
              if (win) {
                setTimeout(() => { try { win.print(); } catch { /* user can hit Ctrl+P */ } }, 900);
              }
            } catch (e) {
              toast.error('Could not open summary: ' + (e.message || 'unknown error'));
            }
          }}
        />
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading…</div>
        ) : filteredJobs.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <Wrench size={48} weight="duotone" className="mx-auto mb-3 opacity-40" />
            <p>{search ? `No job orders match "${search}".` : 'No job orders yet.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">JO #</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Customer / Site</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Service</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Equipment</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Scheduled</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Assignee</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase text-xs">Status</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredJobs.map(j => (
                  <tr key={j.id} className="hover:bg-muted/30" data-testid={`job-order-row-${j.id}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{j.job_number}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{j.customer_name || '—'}</div>
                      {j.site_location && <div className="text-xs text-muted-foreground">{j.site_location}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={j.service_type === 'ppm' ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-slate-50 text-slate-700 border-slate-300'}>
                        {j.service_type === 'ppm' ? 'PPM' : 'One-time'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">{(j.equipment_types || []).join(', ')}</td>
                    <td className="px-3 py-2 text-xs">{j.scheduled_date || '—'}</td>
                    <td className="px-3 py-2 text-xs">{j.assigned_to_name || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-start gap-0.5">
                        <Badge variant="outline" className={STATUS_COLOR[j.status] || ''}>{(j.status || '').replace(/_/g, ' ')}</Badge>
                        {j.custom_status && (
                          <span className="text-[10px] text-muted-foreground italic">&ldquo;{j.custom_status}&rdquo;</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openView(j)} data-testid={`view-jo-${j.id}`} title="View (read-only)">
                        <Eye size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(j)} data-testid={`edit-jo-${j.id}`} title="Edit">
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(j)} title="Download PDF" data-testid={`pdf-jo-${j.id}`}>
                        <FilePdf size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => whatsappShare(j)} title="WhatsApp share" data-testid={`wa-jo-${j.id}`}>
                        <WhatsappLogo size={14} className="text-emerald-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteJob(j)} title="Delete" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40" data-testid={`delete-jo-${j.id}`}>
                        <Trash size={14} />
                      </Button>
                      {j.status === 'draft' && (
                        <Button size="sm" variant="default" onClick={() => callAction(j, 'assign', `?assigned_to_name=${encodeURIComponent(j.assigned_to_name || 'Site Team')}`)} data-testid={`assign-jo-${j.id}`}>
                          <ArrowRight size={12} className="mr-1" /> Assign
                        </Button>
                      )}
                      {j.status === 'assigned' && (
                        <Button size="sm" variant="default" onClick={() => callAction(j, 'start')} data-testid={`start-jo-${j.id}`}>Start</Button>
                      )}
                      {j.status === 'in_progress' && (
                        <Button size="sm" variant="default" onClick={() => callAction(j, 'complete')} data-testid={`complete-jo-${j.id}`}>Mark Complete</Button>
                      )}
                      {j.status === 'completed_by_site' && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => callAction(j, 'approve')} data-testid={`approve-jo-${j.id}`}><Check size={12} className="mr-1" />Approve</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* =============== JOB ORDER FORM =============== */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="job-order-form">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench size={20} weight="duotone" />
              {editing ? `Job Order ${editing.job_number}` : 'New Job Order'}
              {editing && <Badge variant="outline" className={STATUS_COLOR[editing.status] || ''}>{(editing.status || '').replace(/_/g, ' ')}</Badge>}
              {viewOnly && <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-400/40 dark:text-amber-300">View only</Badge>}
            </DialogTitle>
            <DialogDescription>
              Pick a customer, equipment and scope. Build the checklist (manually or with AI). Method of Statement &amp; Risk Assessment are optional.
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-border mb-2 mt-1 flex-shrink-0 overflow-x-auto">
            {[
              { v: 'details', label: '1. Details', icon: Wrench },
              { v: 'checklist', label: `2. Checklist (${form.checklist.length})`, icon: ListChecks },
              { v: 'mos', label: '3. MoS & Risk (Optional)', icon: Shield },
              { v: 'service-reports', label: `4. Service Reports (${serviceReports.length})`, icon: ClipboardText, disabled: !editing },
            ].map(t => (
              <button
                key={t.v}
                onClick={() => !t.disabled && setActiveTab(t.v)}
                disabled={t.disabled}
                className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition ${
                  activeTab === t.v ? 'border-blue-600 text-blue-700 dark:text-blue-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                } ${t.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                data-testid={`jo-tab-${t.v}`}
              >
                <t.icon size={12} className="inline mr-1" /> {t.label}
              </button>
            ))}
          </div>

          <ScrollArea className="flex-1 pr-3">
            {viewOnly && (
              <div className="mb-2 rounded-md border border-amber-400/40 bg-amber-500/10 text-amber-800 dark:text-amber-200 px-3 py-1.5 text-xs flex items-center gap-2" data-testid="jo-viewonly-banner">
                <Eye size={12} /> Read-only view — switch to Edit from the list to make changes.
              </div>
            )}
            <fieldset disabled={viewOnly} className={viewOnly ? 'opacity-95 [&_button]:pointer-events-auto' : ''}>
            {/* ============ TAB: DETAILS ============ */}
            {activeTab === 'details' && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="lg:col-span-2">
                    <Label>Customer <span className="text-red-500">*</span></Label>
                    <CustomerCombobox
                      customers={customers}
                      value={form.customer_id}
                      onPick={pickCustomer}
                      onAdd={(prefill) => { setCustEditing(prefill ? { name: prefill } : null); setCustEditorOpen(true); }}
                      onEdit={(c) => { setCustEditing(c); setCustEditorOpen(true); }}
                    />
                  </div>
                  <div>
                    <Label>Site location</Label>
                    <Input value={form.site_location} onChange={e => setForm({ ...form, site_location: e.target.value })} data-testid="jo-site" />
                  </div>
                  <div>
                    <Label>Service type</Label>
                    <Select value={form.service_type} onValueChange={v => setForm({ ...form, service_type: v })}>
                      <SelectTrigger data-testid="jo-service-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{SERVICE_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Scheduled start</Label>
                    <Input type="date" value={form.scheduled_start_date || form.scheduled_date || ''} onChange={e => setForm({ ...form, scheduled_start_date: e.target.value, scheduled_date: e.target.value })} data-testid="jo-sched-start" />
                  </div>
                  <div>
                    <Label>Scheduled end</Label>
                    <Input type="date" value={form.scheduled_end_date || ''} onChange={e => setForm({ ...form, scheduled_end_date: e.target.value })} data-testid="jo-sched-end" />
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Actual start</Label>
                    <Input type="date" value={form.actual_start_date || ''} onChange={e => setForm({ ...form, actual_start_date: e.target.value })} data-testid="jo-actual-start" />
                  </div>
                  <div>
                    <Label>Actual end</Label>
                    <Input type="date" value={form.actual_end_date || ''} onChange={e => setForm({ ...form, actual_end_date: e.target.value })} data-testid="jo-actual-end" />
                  </div>
                  <div>
                    <Label>Assigned to (Company user)</Label>
                    <Select
                      value={form.assigned_to_user_id || 'none'}
                      onValueChange={v => {
                        if (v === 'none') { setForm({ ...form, assigned_to_user_id: '', assigned_to_user_name: '' }); return; }
                        const u = companyUsers.find(x => x.id === v);
                        setForm({ ...form, assigned_to_user_id: v, assigned_to_user_name: u?.name || '' });
                      }}
                    >
                      <SelectTrigger data-testid="jo-assigned-user"><SelectValue placeholder="(none)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">(none)</SelectItem>
                        {companyUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}{u.role ? ` · ${u.role}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Site team / sub-contractor</Label>
                    <Input value={form.assigned_to_name} onChange={e => setForm({ ...form, assigned_to_name: e.target.value })} placeholder="Free-text team name" data-testid="jo-assignee" />
                  </div>
                  <div>
                    <Label>Customer LPO #</Label>
                    <Input value={form.linked_lpo_number} onChange={e => setForm({ ...form, linked_lpo_number: e.target.value })} placeholder="(optional)" />
                  </div>
                  <div>
                    <Label>Custom status label</Label>
                    <Input
                      value={form.custom_status || ''}
                      list="jo-status-presets"
                      onChange={e => setForm({ ...form, custom_status: e.target.value })}
                      placeholder="e.g. Waiting for Material"
                      data-testid="jo-custom-status"
                    />
                    <datalist id="jo-status-presets">
                      <option value="Assigned" />
                      <option value="WIP" />
                      <option value="Waiting for Material" />
                      <option value="Waiting for Approval" />
                      <option value="On Hold" />
                      <option value="Completed" />
                    </datalist>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Free-text label shown on the PDF alongside the workflow status.</p>
                  </div>
                  <div>
                    <Label>PDF layout</Label>
                    <Select value={form.layout_template} onValueChange={v => setForm({ ...form, layout_template: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LAYOUTS.map(l => <SelectItem key={l} value={l}>{l.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    Equipment <span className="text-xs text-muted-foreground">(multi-select, required for AI checklist)</span>
                    <button type="button" onClick={addCustomEquipment} className="ml-2 text-xs text-blue-600 hover:underline" data-testid="jo-add-custom-equipment">+ custom</button>
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {allEquipment.map(eq => (
                      <button key={eq} type="button" onClick={() => toggleEquipment(eq)}
                              className={`px-3 py-1 rounded-full text-xs border transition ${form.equipment_types.includes(eq) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700'}`}
                              data-testid={`eq-${eq}`}>
                        {form.equipment_types.includes(eq) ? '✓ ' : ''}{eq}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Scope of work</Label>
                  <Textarea rows={3} value={form.scope_of_work} onChange={e => setForm({ ...form, scope_of_work: e.target.value })} data-testid="jo-scope" placeholder="e.g. Quarterly PPM for AHUs in basement plant room…" />
                </div>
                <div>
                  <Label>Safety notes</Label>
                  <Textarea rows={2} value={form.safety_notes} onChange={e => setForm({ ...form, safety_notes: e.target.value })} placeholder="LOTO, height restrictions, hot-work permit needed, etc." />
                </div>
                <div>
                  <Label>Status notes</Label>
                  <Textarea rows={2} value={form.status_notes || ''} onChange={e => setForm({ ...form, status_notes: e.target.value })} placeholder="Optional note describing current status, blockers, next steps." data-testid="jo-status-notes" />
                </div>

                {/* ============ LINKED INVOICES (many) ============ */}
                <div className="border rounded-md p-3 bg-emerald-50/30 dark:bg-emerald-900/10">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="m-0 flex items-center gap-1"><FilePdf size={14} /> Linked Invoices ({(form.linked_invoices || []).length})</Label>
                    <Select
                      value="__pick__"
                      onValueChange={v => {
                        if (v === '__pick__' || !v) return;
                        const inv = invoices.find(i => i.id === v);
                        if (!inv) return;
                        if ((form.linked_invoices || []).some(li => li.id === v)) { toast.info('Already linked'); return; }
                        setForm(f => ({ ...f, linked_invoices: [...(f.linked_invoices || []), { id: inv.id, number: inv.document_number, amount: inv.total || inv.final_payable || 0 }] }));
                      }}
                    >
                      <SelectTrigger className="w-64 h-8 text-xs" data-testid="jo-link-invoice-select">
                        <SelectValue placeholder="+ Link an invoice…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__pick__" disabled>+ Link an invoice…</SelectItem>
                        {invoices.slice(0, 100).map(i => (
                          <SelectItem key={i.id} value={i.id}>{i.document_number} · {i.customer_name} · {i.total}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(form.linked_invoices || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No invoices linked yet.</div>
                  ) : (
                    <table className="w-full text-xs border border-border rounded">
                      <thead className="bg-muted/30"><tr><th className="text-left px-2 py-1">Invoice #</th><th className="text-right px-2 py-1">Amount</th><th className="w-10"></th></tr></thead>
                      <tbody className="divide-y divide-border">
                        {(form.linked_invoices || []).map((li, i) => (
                          <tr key={li.id || i}>
                            <td className="px-2 py-1 font-mono">{li.number}</td>
                            <td className="px-2 py-1 text-right">{li.amount ?? ''}</td>
                            <td className="text-right">
                              <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => setForm(f => ({ ...f, linked_invoices: (f.linked_invoices || []).filter((_, j) => j !== i) }))} data-testid={`jo-unlink-inv-${i}`}>
                                <Trash size={12} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* ============ LINKED VOUCHERS (many) ============ */}
                <div className="border rounded-md p-3 bg-blue-50/30 dark:bg-blue-900/10">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="m-0 flex items-center gap-1"><ClipboardText size={14} /> Linked Payment Vouchers ({(form.linked_vouchers || []).length})</Label>
                    <Select
                      value="__pick__"
                      onValueChange={v => {
                        if (v === '__pick__' || !v) return;
                        const vch = vouchers.find(x => x.id === v);
                        if (!vch) return;
                        if ((form.linked_vouchers || []).some(lv => lv.id === v)) { toast.info('Already linked'); return; }
                        setForm(f => ({ ...f, linked_vouchers: [...(f.linked_vouchers || []), { id: vch.id, number: vch.voucher_number || vch.number, type: 'payment', amount: vch.total_amount || vch.amount || 0 }] }));
                      }}
                    >
                      <SelectTrigger className="w-64 h-8 text-xs" data-testid="jo-link-voucher-select">
                        <SelectValue placeholder="+ Link a payment voucher…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__pick__" disabled>+ Link a payment voucher…</SelectItem>
                        {vouchers.slice(0, 100).map(vch => (
                          <SelectItem key={vch.id} value={vch.id}>{vch.voucher_number || vch.number} · {vch.payee_name || vch.beneficiary || ''} · {vch.total_amount || vch.amount || ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(form.linked_vouchers || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No vouchers linked yet.</div>
                  ) : (
                    <table className="w-full text-xs border border-border rounded">
                      <thead className="bg-muted/30"><tr><th className="text-left px-2 py-1">Voucher #</th><th className="text-left px-2 py-1">Type</th><th className="text-right px-2 py-1">Amount</th><th className="w-10"></th></tr></thead>
                      <tbody className="divide-y divide-border">
                        {(form.linked_vouchers || []).map((lv, i) => (
                          <tr key={lv.id || i}>
                            <td className="px-2 py-1 font-mono">{lv.number}</td>
                            <td className="px-2 py-1">{lv.type || 'payment'}</td>
                            <td className="px-2 py-1 text-right">{lv.amount ?? ''}</td>
                            <td className="text-right">
                              <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => setForm(f => ({ ...f, linked_vouchers: (f.linked_vouchers || []).filter((_, j) => j !== i) }))} data-testid={`jo-unlink-vch-${i}`}>
                                <Trash size={12} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* ============ MATERIALS REQUIRED ============ */}
                <div className="border rounded-md p-3 bg-orange-50/30 dark:bg-orange-900/10">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="m-0">Materials Required ({(form.materials || []).length})</Label>
                    <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, materials: [...(f.materials || []), { item: '', quantity: '', unit: '', remarks: '' }] }))} data-testid="jo-add-material">
                      <Plus size={12} className="mr-1" /> Add material
                    </Button>
                  </div>
                  {(form.materials || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No materials added yet.</div>
                  ) : (
                    <table className="w-full text-xs border border-border rounded">
                      <thead className="bg-muted/30"><tr>
                        <th className="text-left px-2 py-1">Item</th>
                        <th className="text-left px-2 py-1 w-24">Qty</th>
                        <th className="text-left px-2 py-1 w-24">Unit</th>
                        <th className="text-left px-2 py-1">Remarks</th>
                        <th className="w-8"></th>
                      </tr></thead>
                      <tbody className="divide-y divide-border">
                        {(form.materials || []).map((m, i) => (
                          <tr key={i}>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={m.item || ''} onChange={e => setForm(f => ({ ...f, materials: f.materials.map((x, j) => j === i ? { ...x, item: e.target.value } : x) }))} data-testid={`jo-mat-item-${i}`} /></td>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={m.quantity || ''} onChange={e => setForm(f => ({ ...f, materials: f.materials.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x) }))} data-testid={`jo-mat-qty-${i}`} /></td>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={m.unit || ''} onChange={e => setForm(f => ({ ...f, materials: f.materials.map((x, j) => j === i ? { ...x, unit: e.target.value } : x) }))} placeholder="pcs, m, kg…" /></td>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={m.remarks || ''} onChange={e => setForm(f => ({ ...f, materials: f.materials.map((x, j) => j === i ? { ...x, remarks: e.target.value } : x) }))} /></td>
                            <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => setForm(f => ({ ...f, materials: f.materials.filter((_, j) => j !== i) }))} data-testid={`jo-mat-del-${i}`}><Trash size={12} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* ============ TEAM DEPUTED ============ */}
                <div className="border rounded-md p-3 bg-violet-50/30 dark:bg-violet-900/10">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="m-0">Team Deputed ({(form.team_members || []).length})</Label>
                    <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, team_members: [...(f.team_members || []), { name: '', phone: '', role: '', remarks: '' }] }))} data-testid="jo-add-team">
                      <Plus size={12} className="mr-1" /> Add team member
                    </Button>
                  </div>
                  {(form.team_members || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No team members added yet.</div>
                  ) : (
                    <table className="w-full text-xs border border-border rounded">
                      <thead className="bg-muted/30"><tr>
                        <th className="text-left px-2 py-1">Name</th>
                        <th className="text-left px-2 py-1 w-40">Role</th>
                        <th className="text-left px-2 py-1 w-36">Phone</th>
                        <th className="text-left px-2 py-1">Remarks</th>
                        <th className="w-8"></th>
                      </tr></thead>
                      <tbody className="divide-y divide-border">
                        {(form.team_members || []).map((t, i) => (
                          <tr key={i}>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={t.name || ''} onChange={e => setForm(f => ({ ...f, team_members: f.team_members.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))} data-testid={`jo-team-name-${i}`} /></td>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={t.role || ''} onChange={e => setForm(f => ({ ...f, team_members: f.team_members.map((x, j) => j === i ? { ...x, role: e.target.value } : x) }))} placeholder="Technician / Foreman…" /></td>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={t.phone || ''} onChange={e => setForm(f => ({ ...f, team_members: f.team_members.map((x, j) => j === i ? { ...x, phone: e.target.value } : x) }))} placeholder="+971…" data-testid={`jo-team-phone-${i}`} /></td>
                            <td className="px-1 py-1"><Input className="h-7 text-xs" value={t.remarks || ''} onChange={e => setForm(f => ({ ...f, team_members: f.team_members.map((x, j) => j === i ? { ...x, remarks: e.target.value } : x) }))} /></td>
                            <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => setForm(f => ({ ...f, team_members: f.team_members.filter((_, j) => j !== i) }))} data-testid={`jo-team-del-${i}`}><Trash size={12} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* ============ TAB: CHECKLIST ============ */}
            {activeTab === 'checklist' && (
              <div className="space-y-3 py-2">
                <div className="border border-blue-200 bg-blue-50/40 dark:bg-blue-900/10 rounded-md p-3 text-xs">
                  <strong>Build the checklist for site technicians.</strong> Pick an equipment from the dropdown below to let AI auto-generate items, or click "+ Add row" to enter them manually. Site teams will tick these off in the Service Report.
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Select onValueChange={(eq) => aiChecklist(eq)} disabled={aiBusy.checklist}>
                    <SelectTrigger className="w-64 h-9 text-xs" data-testid="jo-ai-checklist-eq">
                      <Sparkle size={12} className="mr-1" />
                      <SelectValue placeholder={aiBusy.checklist ? 'Generating…' : 'AI add for equipment…'} />
                    </SelectTrigger>
                    <SelectContent>
                      {allEquipment.map(eq => <SelectItem key={eq} value={eq}>{eq}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={addChecklistRow} data-testid="jo-add-checklist-row">
                    <Plus size={12} className="mr-1" /> Add row manually
                  </Button>
                  {form.checklist.length > 0 && (
                    <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => setForm(f => ({ ...f, checklist: [] }))}>
                      Clear all
                    </Button>
                  )}
                </div>

                {form.checklist.length === 0 ? (
                  <div className="border border-dashed rounded-md p-8 text-center text-sm text-muted-foreground">
                    <ListChecks size={32} weight="duotone" className="mx-auto mb-2 opacity-40" />
                    No checklist items yet. Use "AI add for equipment…" or "Add row manually" above.
                  </div>
                ) : (
                  <table className="w-full text-xs border border-border rounded-md">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="text-left px-2 py-1 w-32">Section</th>
                        <th className="text-center w-10">✓</th>
                        <th className="text-left px-2 py-1">Item / Task</th>
                        <th className="text-left px-2 py-1 w-44">Comment</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {form.checklist.map((c, i) => (
                        <tr key={c.id || i}>
                          <td className="px-2 py-1"><Input value={c.section || ''} onChange={e => updateChecklistRow(i, 'section', e.target.value)} className="h-7 text-xs" /></td>
                          <td className="text-center">
                            <button type="button" onClick={() => updateChecklistRow(i, 'checked', !c.checked)} className="text-lg">
                              {c.checked ? <CheckSquare size={18} weight="fill" className="text-emerald-600" /> : <Square size={18} />}
                            </button>
                          </td>
                          <td className="px-2 py-1"><Input value={c.description || ''} onChange={e => updateChecklistRow(i, 'description', e.target.value)} className="h-7 text-xs" /></td>
                          <td className="px-2 py-1"><Input value={c.comment || ''} onChange={e => updateChecklistRow(i, 'comment', e.target.value)} className="h-7 text-xs" /></td>
                          <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removeChecklistRow(i)}><Trash size={12} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ============ TAB: MoS & RA (OPTIONAL) ============ */}
            {activeTab === 'mos' && (
              <div className="space-y-3 py-2">
                <div className="border border-amber-200 bg-amber-50/40 dark:bg-amber-900/10 rounded-md p-3 text-xs">
                  <strong>Optional.</strong> Method of Statement and Risk Assessment are not mandatory — skip them and just save the job card if not needed. Use the AI draft buttons to generate UAE OSHAD-style documents.
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="border rounded-md p-3 bg-blue-50/30 dark:bg-blue-900/10">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="flex items-center gap-1 m-0">
                        <Shield size={14} /> Method of Statement
                        <Badge variant="outline" className="text-[9px] ml-1">Optional</Badge>
                      </Label>
                      <Button size="sm" variant="outline" disabled={aiBusy.mos} onClick={aiMoS} data-testid="jo-ai-mos">
                        <Sparkle size={12} className="mr-1" /> {aiBusy.mos ? 'Generating…' : 'AI draft'}
                      </Button>
                    </div>
                    <Textarea rows={12} value={form.method_of_statement} onChange={e => setForm({ ...form, method_of_statement: e.target.value })}
                              placeholder="Type or click AI draft" className="font-mono text-xs" />
                  </div>
                  <div className="border rounded-md p-3 bg-amber-50/30 dark:bg-amber-900/10">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="flex items-center gap-1 m-0">
                        <Warning size={14} /> Risk Assessment
                        <Badge variant="outline" className="text-[9px] ml-1">Optional</Badge>
                      </Label>
                      <Button size="sm" variant="outline" disabled={aiBusy.ra} onClick={aiRA} data-testid="jo-ai-ra">
                        <Sparkle size={12} className="mr-1" /> {aiBusy.ra ? 'Generating…' : 'AI draft'}
                      </Button>
                    </div>
                    <Textarea rows={12} value={form.risk_assessment} onChange={e => setForm({ ...form, risk_assessment: e.target.value })}
                              placeholder="Type or click AI draft" className="font-mono text-xs" />
                  </div>
                </div>
              </div>
            )}

            {/* ============ TAB: SERVICE REPORTS ============ */}
            {activeTab === 'service-reports' && (
              <div className="space-y-3 py-2">
                <div className="border border-violet-200 bg-violet-50/40 dark:bg-violet-900/10 rounded-md p-3 text-xs">
                  <strong>Service Reports</strong> are filled by the site team AFTER the work is done. They carry the same checklist + work done details, materials used, attendants, signatures, and before/after photos. Generate a fresh PDF for the customer to sign on site.
                </div>
                <Button onClick={openNewServiceReport} size="sm" data-testid="jo-new-sr">
                  <Plus size={14} className="mr-1" /> New Service Report
                </Button>
                {serviceReports.length === 0 ? (
                  <div className="border border-dashed rounded-md p-8 text-center text-sm text-muted-foreground">
                    <ClipboardText size={32} weight="duotone" className="mx-auto mb-2 opacity-40" />
                    No Service Reports yet. Click "New Service Report" to create one.
                  </div>
                ) : (
                  <table className="w-full text-xs border border-border rounded-md">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="text-left px-2 py-1">SR #</th>
                        <th className="text-left px-2 py-1">Date</th>
                        <th className="text-left px-2 py-1">Status</th>
                        <th className="text-left px-2 py-1">Customer Rep</th>
                        <th className="text-right px-2 py-1">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {serviceReports.map(sr => (
                        <tr key={sr.id}>
                          <td className="px-2 py-1 font-mono font-semibold">{sr.sr_number}</td>
                          <td className="px-2 py-1">{sr.service_date || '—'}</td>
                          <td className="px-2 py-1">
                            <Badge variant="outline" className="text-[10px]">{sr.status || 'draft'}</Badge>
                          </td>
                          <td className="px-2 py-1">{sr.customer_rep_name || '—'}</td>
                          <td className="px-2 py-1 text-right space-x-1">
                            <Button size="sm" variant="ghost" onClick={() => openServiceReport(sr)} data-testid={`open-sr-${sr.id}`}>Open</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ============ Pictures section (visible in all tabs after save) ============ */}
            {editing && activeTab !== 'service-reports' && (
              <div className="mt-4 pt-4 border-t">
                <Label className="mb-2 block text-xs uppercase font-semibold text-muted-foreground">Job Order pictures & attachments</Label>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  {['before', 'after', 'attachment'].map(kind => {
                    const list = kind === 'before' ? (editing.before_pictures || [])
                              : kind === 'after' ? (editing.after_pictures || [])
                              : (editing.attachments || []);
                    return (
                      <div key={kind} className="border rounded-md p-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="flex items-center gap-1 m-0 capitalize text-xs">
                            <Camera size={12} /> {kind === 'attachment' ? 'Attachments' : `${kind} pictures`} ({list.length})
                          </Label>
                          <label className="cursor-pointer">
                            <input type="file" className="hidden"
                                   accept={kind === 'attachment' ? '*' : 'image/*'}
                                   onChange={e => e.target.files?.[0] && uploadFile(kind, e.target.files[0])}
                                   data-testid={`jo-upload-${kind}`} />
                            <Button size="sm" variant="outline" type="button" onClick={(e) => { e.preventDefault(); e.currentTarget.previousElementSibling.click(); }}>
                              <UploadSimple size={12} className="mr-1" /> Upload
                            </Button>
                          </label>
                        </div>
                        <div className="space-y-1 max-h-32 overflow-auto">
                          {list.map(a => (
                            <div key={a.id} className="text-xs flex items-center justify-between">
                              <span className="truncate">📎 {a.filename}</span>
                            </div>
                          ))}
                          {list.length === 0 && <div className="text-xs text-muted-foreground italic">None</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </fieldset>
          </ScrollArea>

          <DialogFooter className="border-t pt-3 flex-shrink-0 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Close</Button>
            {editing && (
              <>
                <Button variant="outline" onClick={() => whatsappShare(editing)} data-testid="jo-whatsapp">
                  <WhatsappLogo size={14} className="mr-1 text-emerald-600" /> WhatsApp
                </Button>
                <Button variant="outline" onClick={() => downloadPdf(editing)} data-testid="jo-pdf-download">
                  <FilePdf size={14} className="mr-1" /> Download PDF
                </Button>
              </>
            )}
            {!viewOnly && (
              <>
                <Button variant="outline" onClick={saveAndDownloadPdf} disabled={working} data-testid="jo-save-pdf">
                  <FilePdf size={14} className="mr-1" /> Save &amp; Download PDF
                </Button>
                <Button onClick={save} disabled={working} data-testid="jo-save">
                  <FloppyDisk size={14} className="mr-1" /> {editing ? 'Update' : 'Save'}
                </Button>
              </>
            )}
            {viewOnly && editing && (
              <Button variant="default" onClick={() => { setViewOnly(false); }} data-testid="jo-switch-to-edit">
                <PencilSimple size={14} className="mr-1" /> Switch to Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerEditorDialog
        open={custEditorOpen}
        onOpenChange={setCustEditorOpen}
        existing={custEditing}
        onSaved={(c) => {
          setCustomers(prev => {
            const i = prev.findIndex(x => x.id === c.id);
            if (i >= 0) { const cp = [...prev]; cp[i] = c; return cp; }
            return [...prev, c];
          });
          pickCustomer(c);
        }}
      />

      <ServiceReportDialog
        open={srOpen}
        onOpenChange={(v) => { setSrOpen(v); if (!v) refreshServiceReports(); }}
        srId={srEditingId}
      />
    </div>
  );
}
